package globals

import accelbyte.user.getUserAccessToken
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.ttt.autogenesis.config.ConfigSource
import org.ttt.autogenesis.fleetcontrol.client.fleetControlAuthorizationHeaderValue
import org.ttt.autogenesis.fleetcontrol.protocol.EnsureFleetReadyResponse
import org.ttt.autogenesis.fleetcontrol.protocol.FleetControlRoutes
import org.ttt.autogenesis.fleetcontrol.protocol.FleetReadinessState
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

private const val FLEET_CONTROL_API_RUNTIME_PROPERTY = "__AUTOGENESIS_FLEET_CONTROL_API_BASE_URL__"
private const val FLEET_CONTROL_TIMEOUT_RUNTIME_PROPERTY = "__AUTOGENESIS_FLEET_CONTROL_TIMEOUT_MS__"
private const val DEFAULT_FLEET_CONTROL_TIMEOUT_MILLIS = 180_000L
private const val DEFAULT_FLEET_CONTROL_POLL_INTERVAL_MILLIS = 1_000L

/**
 * Resolves the Fleet Control API URL from an explicit value, browser runtime
 * configuration, or an environment-provided fallback.
 *
 * @param explicitValue Highest-priority configured URL.
 * @param environmentValue Build or deployment environment fallback.
 * @return Normalized API base URL, or `null` when Fleet Control is disabled.
 */
internal fun resolveFleetControlApiBaseUrl(
    explicitValue: String?,
    environmentValue: String?,
): String?
{
    val runtimeValue: dynamic = js("typeof window !== 'undefined' ? window.__AUTOGENESIS_FLEET_CONTROL_API_BASE_URL__ : null")
    return (explicitValue?.takeIf { it.isNotBlank() }
        ?: (runtimeValue as? String)?.takeIf { it.isNotBlank() }
        ?: environmentValue?.takeIf { it.isNotBlank() })?.trimEnd('/')
}

/**
 * Browser-side Fleet Control runtime configuration.
 */
object FleetControlBrowserConfig
{
    /**
     * Returns the configured Fleet Control API base URL.
     */
    val apiBaseUrl: String?
        get() = resolveFleetControlApiBaseUrl(null, configuredFleetControlApiBaseUrl())

    /**
     * Returns the readiness polling timeout from browser runtime configuration.
     */
    val timeoutMillis: Long
        get() = resolveRuntimeLong(DEFAULT_FLEET_CONTROL_TIMEOUT_MILLIS)
            .coerceAtMost(DEFAULT_FLEET_CONTROL_TIMEOUT_MILLIS)

    /**
     * Returns the default readiness polling interval.
     */
    val pollIntervalMillis: Long = DEFAULT_FLEET_CONTROL_POLL_INTERVAL_MILLIS
}

private fun configuredFleetControlApiBaseUrl(): String?
{
    return ConfigSource.propertyOrEmpty(
        "kvision-iam.local.properties",
        "kvision.fleetControl.apiBaseUrl",
    ).takeIf { it.isNotBlank() }
}

/**
 * Polls Fleet Control readiness before a browser starts live matchmaking.
 *
 * @param apiBaseUrl Fleet Control base URL, or `null` to bypass the gate for local mode.
 * @param pollIntervalMillis Minimum delay between readiness requests.
 * @param timeoutMillis Maximum time spent waiting for readiness.
 * @param request Injectable request function for browser tests.
 * @param delayFunction Injectable delay function for browser tests.
 * @param clock Injectable monotonic-enough clock for timeout tests.
 * @param accessTokenProvider Current browser bearer-token provider.
 * @param onStarting Called whenever Fleet Control reports that startup is still in progress.
 */
class FleetControlReadinessClient(
    private val apiBaseUrl: String?,
    private val pollIntervalMillis: Long = DEFAULT_FLEET_CONTROL_POLL_INTERVAL_MILLIS,
    private val timeoutMillis: Long = DEFAULT_FLEET_CONTROL_TIMEOUT_MILLIS,
    private val request: suspend (String, String?) -> EnsureFleetReadyResponse = ::requestFleetReadiness,
    private val delayFunction: suspend (Long) -> Unit = { delay(it) },
    private val clock: () -> Long = { kotlinx.browser.window.performance.now().toLong() },
    private val accessTokenProvider: () -> String? = ::getUserAccessToken,
    private val onStarting: (Long) -> Unit = {},
)
{
    init
    {
        require(pollIntervalMillis >= 0L) { "pollIntervalMillis cannot be negative" }
        require(timeoutMillis > 0L) { "timeoutMillis must be positive" }
    }

    /**
     * Waits until Fleet Control reports `READY`.
     *
     * A missing API URL is the explicit local-mode bypass. `ERROR`, request
     * failures, and timeout return `false` so callers do not invoke matchmaking.
     *
     * @return `true` when live matchmaking may proceed.
     */
    suspend fun awaitReady(): Boolean
    {
        return awaitReadyResponse(FleetControlRoutes.ENSURE_SERVER_EXTEND_READY).state == FleetReadinessState.READY
    }

    /**
     * Polls the role-specific public bootstrap route and returns its endpoint
     * lease. This is the live browser entrypoint; it must complete before the
     * server-extend bridge is opened.
     *
     * @return The final readiness response.
     */
    suspend fun awaitServerExtendReady(): EnsureFleetReadyResponse =
        awaitReadyResponse(FleetControlRoutes.ENSURE_SERVER_EXTEND_READY)

    private suspend fun awaitReadyResponse(route: String): EnsureFleetReadyResponse
    {
        if(apiBaseUrl.isNullOrBlank())
        {
            return EnsureFleetReadyResponse(FleetReadinessState.READY)
        }
        val normalizedUrl = apiBaseUrl.trimEnd('/') + route
        val startedAtMillis = clock()
        val deadlineMillis = startedAtMillis + timeoutMillis
        while(true)
        {
            val remainingMillis = deadlineMillis - clock()
            if(remainingMillis <= 0L)
            {
                break
            }

            val response = try
            {
                withTimeoutOrNull(remainingMillis) {
                    request(normalizedUrl, accessTokenProvider())
                } ?: return EnsureFleetReadyResponse(FleetReadinessState.ERROR, message = "readiness timeout")
            }
            catch(cancelled: kotlinx.coroutines.CancellationException)
            {
                throw cancelled
            }
            catch(_: Throwable)
            {
                return EnsureFleetReadyResponse(FleetReadinessState.ERROR, message = "readiness request failed")
            }
            when(response.state)
            {
                FleetReadinessState.READY -> return response
                FleetReadinessState.ERROR -> return response
                FleetReadinessState.STARTING ->
                {
                    onStarting((clock() - startedAtMillis).coerceAtLeast(0L))
                    val retryDelayMillis = maxOf(
                        pollIntervalMillis,
                        response.retryAfterSeconds.coerceAtLeast(0) * 1_000L,
                    )
                    val remainingAfterRequestMillis = deadlineMillis - clock()
                    if(remainingAfterRequestMillis <= 0L)
                    {
                        break
                    }
                    delayFunction(minOf(maxOf(1L, retryDelayMillis), remainingAfterRequestMillis))
                }
            }
        }
        return EnsureFleetReadyResponse(FleetReadinessState.ERROR, message = "readiness timeout")
    }
}

private suspend fun requestFleetReadiness(
    url: String,
    accessToken: String?,
): EnsureFleetReadyResponse
{
    val headers = Headers().apply {
        append("Accept", "application/json")
        if(!accessToken.isNullOrBlank())
        {
            append("Authorization", fleetControlAuthorizationHeaderValue(accessToken))
        }
    }
    val response = window.fetch(
        url,
        RequestInit(method = "POST", headers = headers),
    ).await()
    if(!response.ok)
    {
        return EnsureFleetReadyResponse(FleetReadinessState.ERROR, message = "readiness request failed")
    }
    return Json.decodeFromString(response.text().await())
}

private fun resolveRuntimeLong(defaultValue: Long): Long
{
    val runtimeValue: dynamic = js("typeof window !== 'undefined' ? window.__AUTOGENESIS_FLEET_CONTROL_TIMEOUT_MS__ : null")
    return (runtimeValue as? Number)?.toLong()?.takeIf { it > 0L } ?: defaultValue
}
